import { ActionIcon, Box, Button, Select, Table, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { calcLine, formatMoney } from '@erp/shared';
import type { UnitOfMeasure, VatRate } from '../api/catalogs';
import type { Nomenclature } from '../api/nomenclature';

export interface ActLineRow {
  key: string;
  nomenclatureId: string | null;
  name: string;
  article: string | null;
  /** Пустая строка — единица не указана. В акте это допустимо. */
  unitId: string;
  quantity: string;
  price: string;
  vatRateId: string;
}

export function emptyActLineRow(): ActLineRow {
  return {
    key: crypto.randomUUID(),
    nomenclatureId: null,
    name: '',
    article: '',
    unitId: '',
    quantity: '1',
    price: '',
    vatRateId: '',
  };
}

interface Props {
  lines: ActLineRow[];
  onChange: (lines: ActLineRow[]) => void;
  canEdit: boolean;
  nomenclatureOptions: Nomenclature[];
  units: UnitOfMeasure[];
  vatRates: VatRate[];
}

/** Строка = ноль сумм, чтобы не показывать NaN, пока поля не заполнены. */
function calcRowAmounts(row: ActLineRow, vatRates: VatRate[]) {
  const quantity = row.quantity.trim().replace(',', '.');
  const price = row.price.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(quantity) || !/^\d+(\.\d+)?$/.test(price)) {
    return { amount: '0.00', vatAmount: '0.00', totalAmount: '0.00' };
  }
  const vatRate = vatRates.find((r) => r.id === row.vatRateId);
  const amounts = calcLine({ quantity, price, vatRateValue: vatRate?.rate ?? null });
  return {
    amount: amounts.amount.toFixed(2),
    vatAmount: amounts.vatAmount.toFixed(2),
    totalAmount: amounts.totalAmount.toFixed(2),
  };
}

/**
 * Редактор строк акта. Отличается от редактора УПД одним: единица измерения
 * необязательна и очищаема — в акте по услугам её часто не указывают, а код
 * ОКЕИ здесь ни на что не влияет (в XML акт не выгружается).
 */
export function ActLinesEditor({ lines, onChange, canEdit, nomenclatureOptions, units, vatRates }: Props) {
  const nomenclatureSelectOptions = nomenclatureOptions.map((n) => ({ value: n.id, label: n.name }));
  const unitOptions = units.map((u) => ({ value: u.id, label: u.symbol }));
  const vatRateOptions = vatRates.map((r) => ({ value: r.id, label: r.name }));

  function updateRow(key: string, patch: Partial<ActLineRow>) {
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function pickNomenclature(key: string, nomenclatureId: string | null) {
    const item = nomenclatureOptions.find((n) => n.id === nomenclatureId);
    if (!item) {
      updateRow(key, { nomenclatureId: null });
      return;
    }
    updateRow(key, {
      nomenclatureId: item.id,
      name: item.name,
      article: item.article,
      unitId: item.unitId,
      price: item.price ?? '',
      vatRateId: item.vatRateId ?? '',
    });
  }

  function addRow() {
    onChange([...lines, emptyActLineRow()]);
  }

  function removeRow(key: string) {
    onChange(lines.filter((l) => l.key !== key));
  }

  const totals = lines.reduce(
    (acc, row) => {
      const a = calcRowAmounts(row, vatRates);
      return {
        amount: acc.amount + Number(a.amount),
        vatAmount: acc.vatAmount + Number(a.vatAmount),
        totalAmount: acc.totalAmount + Number(a.totalAmount),
      };
    },
    { amount: 0, vatAmount: 0, totalAmount: 0 },
  );

  return (
    <Box>
      <Box style={{ overflowX: 'auto' }}>
        <Table verticalSpacing={6} miw={1100}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>№</Table.Th>
              <Table.Th w={220}>Из номенклатуры</Table.Th>
              <Table.Th>Наименование работ, услуг</Table.Th>
              <Table.Th w={110}>Ед.</Table.Th>
              <Table.Th w={100}>Кол-во</Table.Th>
              <Table.Th w={120}>Цена</Table.Th>
              <Table.Th w={150}>Ставка НДС</Table.Th>
              <Table.Th w={110}>Сумма</Table.Th>
              <Table.Th w={100}>НДС</Table.Th>
              <Table.Th w={110}>Всего</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((row, index) => {
              const amounts = calcRowAmounts(row, vatRates);
              return (
                <Table.Tr key={row.key}>
                  <Table.Td>{index + 1}</Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      placeholder="Выбрать"
                      data={nomenclatureSelectOptions}
                      value={row.nomenclatureId}
                      onChange={(v) => pickNomenclature(row.key, v)}
                      disabled={!canEdit}
                      searchable
                      clearable
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      value={row.name}
                      onChange={(e) => updateRow(row.key, { name: e.currentTarget.value })}
                      disabled={!canEdit}
                      required
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      placeholder="—"
                      data={unitOptions}
                      value={row.unitId || null}
                      onChange={(v) => updateRow(row.key, { unitId: v ?? '' })}
                      disabled={!canEdit}
                      searchable
                      clearable
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      value={row.quantity}
                      onChange={(e) => updateRow(row.key, { quantity: e.currentTarget.value })}
                      disabled={!canEdit}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      value={row.price}
                      onChange={(e) => updateRow(row.key, { price: e.currentTarget.value })}
                      disabled={!canEdit}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      data={vatRateOptions}
                      value={row.vatRateId || null}
                      onChange={(v) => updateRow(row.key, { vatRateId: v ?? '' })}
                      disabled={!canEdit}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{formatMoney(amounts.amount)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{formatMoney(amounts.vatAmount)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" fw={500}>
                      {formatMoney(amounts.totalAmount)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {canEdit && (
                      <ActionIcon variant="subtle" color="red" onClick={() => removeRow(row.key)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Box>

      {canEdit && (
        <Button mt="xs" size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addRow}>
          Добавить строку
        </Button>
      )}

      <Box mt="sm" ta="right">
        <Text size="sm">Сумма без НДС: {formatMoney(totals.amount)}</Text>
        <Text size="sm">НДС: {formatMoney(totals.vatAmount)}</Text>
        <Text size="md" fw={700}>
          Итого: {formatMoney(totals.totalAmount)}
        </Text>
      </Box>
    </Box>
  );
}
